import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Helmet } from "react-helmet";
import Login from "../manage/Login";
import ClientPortalService from "../../services/ClientPortalService";
import { ROLES } from "../common/Const";
import * as userActions from "../../redux/actions/usersActions";

const money = value => `${Number(value || 0).toFixed(2)} zl`;
const fullName = person => [person.firstName, person.lastName].filter(Boolean).join(" ") || person.id;

const EmptyState = ({ children }) => (
  <div className="text-muted py-2">{children}</div>
);

EmptyState.propTypes = {
  children: PropTypes.node.isRequired,
};

export function ClientPortal({ user, checkToken }) {
  const [portal, setPortal] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedCancellation, setSelectedCancellation] = useState(null);
  const [actionError, setActionError] = useState("");
  const [actionInProgress, setActionInProgress] = useState("");

  const canAccess = user && user.role === ROLES.CLIENT && user.clientId;
  const isGuest = !user || user.role === ROLES.GUEST;

  useEffect(() => {
    if (isGuest) {
      checkToken();
    }
  }, [checkToken, isGuest]);

  useEffect(() => {
    if (!canAccess) return;

    setLoading(true);
    ClientPortalService.getClientPortal()
      .then((payload) => {
        setPortal(payload);
        setError("");
      })
      .catch((loadError) => {
        setError(loadError.message);
      })
      .finally(() => setLoading(false));
  }, [canAccess]);

  const refreshPortal = () => (
    ClientPortalService.getClientPortal()
      .then((payload) => {
        setPortal(payload);
        setError("");
      })
  );

  const confirmCancellation = () => {
    if (!selectedCancellation) return Promise.resolve();
    setActionInProgress(selectedCancellation.lessonId);
    setActionError("");
    return ClientPortalService.cancelLessonParticipation({
      lessonId: selectedCancellation.lessonId,
      studentId: selectedCancellation.studentId,
    })
      .then(() => refreshPortal())
      .then(() => setSelectedCancellation(null))
      .catch(actionFailure => setActionError(actionFailure.message))
      .finally(() => setActionInProgress(""));
  };

  const undoCancellation = (lesson) => {
    setActionInProgress(lesson.lessonId);
    setActionError("");
    return ClientPortalService.undoLessonCancellation({
      lessonId: lesson.lessonId,
      studentId: lesson.studentId,
    })
      .then(() => refreshPortal())
      .catch(actionFailure => setActionError(actionFailure.message))
      .finally(() => setActionInProgress(""));
  };

  const isSelectedCancellation = lesson => (
    selectedCancellation &&
    selectedCancellation.lessonId === lesson.lessonId &&
    selectedCancellation.studentId === lesson.studentId
  );

  if (isGuest) {
    return (
      <div className="quiz-page row m-0 p-0 d-flex justify-content-center align-items-center">
        <div className="col-lg-8 col-11">
          <Login />
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="manage-container row m-0 d-flex justify-content-center">
        <div className="col-lg-10 col-11 mt-lg-5 mt-3">
          <div className="alert alert-danger manage-text" role="alert">Brak uprawnien</div>
        </div>
      </div>
    );
  }

  const client = portal && portal.client ? portal.client : {};
  const students = portal && portal.students ? portal.students : [];
  const schedule = portal && portal.schedule ? portal.schedule : [];
  const ledger = portal && portal.ledger ? portal.ledger : { entries: [], balance: 0, paymentsTotal: 0, chargesTotal: 0 };
  const attendance = portal && portal.attendance ? portal.attendance : { summary: { present: 0, absent: 0, total: 0, rate: 0 }, lessons: [] };
  const feedback = portal && portal.feedback ? portal.feedback : [];
  const materials = portal && portal.materials ? portal.materials : [];
  const history = portal && portal.history ? portal.history : { lessons: [], courses: [], invoices: [] };

  return (
    <>
      <Helmet>
        <title>Extra English - portal klienta</title>
      </Helmet>
      <main className="manage-container row m-0 d-flex justify-content-center">
        <div className="col-lg-10 col-11 mt-lg-5 mt-3 mb-5">
          <h1 className="manage-title">Portal klienta</h1>
          {loading && <div className="manage-text">Ladowanie portalu...</div>}
          {error && <div className="alert alert-danger" role="alert">{error}</div>}
          {actionError && <div className="alert alert-danger" role="alert">{actionError}</div>}
          {portal && (
            <>
              <section className="mb-4">
                <div className="manage-subtitle">{fullName(client)}</div>
              </section>

              <div className="row">
                <section className="col-lg-8 mb-4">
                  <h2 className="manage-subtitle">Najblizsze lekcje</h2>
                  {schedule.length === 0 && <EmptyState>Brak nadchodzacych lekcji</EmptyState>}
                  {schedule.slice(0, 5).map(lesson => (
                    <div key={`${lesson.id}-${lesson.studentId}`} className="table-row row mx-0 manage-text">
                      <div className="col-md-3 table-cell">{lesson.date}</div>
                      <div className="col-md-2 table-cell">{lesson.startTime}</div>
                      <div className="col-md-4 table-cell">{lesson.studentName}</div>
                      <div className="col-md-3 table-cell">
                        <div>{lesson.classTypeId}</div>
                        {lesson.canCancel && (
                          <button
                            type="button"
                            className="btn btn-sm manage-button manage-red-button mt-2"
                            disabled={actionInProgress === lesson.lessonId}
                            onClick={() => setSelectedCancellation(lesson)}
                          >
                            Nie bede na zajeciach
                          </button>
                        )}
                        {lesson.canUndoCancellation && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary mt-2"
                            disabled={actionInProgress === lesson.lessonId}
                            onClick={() => undoCancellation(lesson)}
                          >
                            Cofnij odwolanie
                          </button>
                        )}
                        {isSelectedCancellation(lesson) && (
                          <div className="mt-2">
                            <div className="text-muted small">
                              {lesson.cancellationEligibility && lesson.cancellationEligibility.message}
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm manage-button manage-red-button mt-2"
                              disabled={actionInProgress === lesson.lessonId}
                              onClick={confirmCancellation}
                            >
                              Potwierdzam odwolanie
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-link mt-2"
                              onClick={() => setSelectedCancellation(null)}
                            >
                              Anuluj
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </section>

                <aside className="col-lg-4 mb-4">
                  <h2 className="manage-subtitle">Saldo klienta</h2>
                  <div className="manage-text">{money(ledger.balance)}</div>
                  <div className="manage-text">Wplaty: {money(ledger.paymentsTotal)}</div>
                  <div className="manage-text">Naliczenia: {money(ledger.chargesTotal)}</div>
                </aside>
              </div>

              <section className="mb-4">
                <h2 className="manage-subtitle">Studenci</h2>
                {students.length === 0 && <EmptyState>Brak przypisanych studentow</EmptyState>}
                {students.map(student => (
                  <div key={student.id} className="manage-text">{fullName(student)}</div>
                ))}
              </section>

              <section className="mb-4">
                <h2 className="manage-subtitle">Frekwencja</h2>
                <div className="manage-text">
                  Obecnosci: {attendance.summary.present}/{attendance.summary.total} ({attendance.summary.rate}%)
                </div>
              </section>

              <section className="mb-4">
                <h2 className="manage-subtitle">Platnosci</h2>
                {ledger.entries.length === 0 && <EmptyState>Brak platnosci</EmptyState>}
                {ledger.entries.slice(-5).map(entry => (
                  <div key={entry.id} className="manage-text">{entry.date} - {entry.type} - {money(entry.amount)}</div>
                ))}
              </section>

              <section className="mb-4">
                <h2 className="manage-subtitle">Historia</h2>
                {history.lessons.length === 0 && history.courses.length === 0 && history.invoices.length === 0 && (
                  <EmptyState>Brak historii lekcji, kursow i faktur</EmptyState>
                )}
                {(history.lessons.length > 0 || history.courses.length > 0 || history.invoices.length > 0) && (
                  <div className="manage-text">
                    <div>Lekcje: {history.lessons.length}</div>
                    <div>Kursy: {history.courses.length}</div>
                    <div>Faktury: {history.invoices.length}</div>
                  </div>
                )}
              </section>

              <section className="mb-4">
                <h2 className="manage-subtitle">Feedback</h2>
                {feedback.length === 0 && <EmptyState>Brak feedbacku</EmptyState>}
                {feedback.slice(0, 5).map(item => (
                  <div key={`${item.lessonId}-${item.studentId}`} className="manage-text">{item.date} - {item.studentName}: {item.comment}</div>
                ))}
              </section>

              <section className="mb-4">
                <h2 className="manage-subtitle">Materialy</h2>
                {materials.length === 0 && <EmptyState>Brak materialow</EmptyState>}
                {materials.map(item => (
                  <div key={item.id} className="manage-text">{item.title}</div>
                ))}
              </section>
            </>
          )}
        </div>
      </main>
    </>
  );
}

ClientPortal.propTypes = {
  user: PropTypes.object,
  checkToken: PropTypes.func.isRequired,
};

ClientPortal.defaultProps = {
  user: null,
};

const mapStateToProps = state => ({
  user: state.user,
});

const mapDispatchToProps = {
  checkToken: userActions.checkToken,
};

export default connect(mapStateToProps, mapDispatchToProps)(ClientPortal);
